import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Seo, Mark, Container } from '../components/marketing/Primitives';
import { examPages, examOrder, sharedIncluded, showPricing } from '../data/prepData';

export default function PrepExamPage() {
  const { examId } = useParams();
  const exam = examPages[examId];
  const [openFaq, setOpenFaq] = useState(null);

  if (!exam) return <Navigate to="/products/prep" replace />;

  const inDev = exam.status === 'development';
  const others = examOrder.filter((id) => id !== examId);
  const included = [...(exam.extras || []), ...sharedIncluded];

  return (
    <>
      <Seo
        title={`${exam.name} preparation`}
        description={`${exam.tagline} EdAI Prep for the ${exam.fullName}: practice questions, mock exams, flashcards, and an adaptive study plan, every item traced to a physician-certified source.`}
      />

      {/* Hero */}
      <section className="bg-ink text-paper-dark-text">
        <Container className="py-16 md:py-24">
          <p className="text-sm text-slate-dark-text">
            <Link to="/products/prep" className="link-quiet">EdAI Prep</Link>
            <span aria-hidden="true"> / </span>
            {exam.jurisdiction}
          </p>
          <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <h1 className="hero-rise font-serif text-4xl leading-tight md:text-5xl">{exam.name}</h1>
            {inDev ? (
              <p className="rounded border border-hairline px-2.5 py-1 text-xs font-semibold text-slate-dark-text">
                In development
              </p>
            ) : (
              showPricing && exam.price && <p className="text-lg font-semibold text-seal">{exam.price}</p>
            )}
          </div>
          <p className="hero-rise-2 mt-2 text-lg text-slate-dark-text">{exam.fullName}</p>
          <p className="hero-rise-2 mt-5 max-w-measure text-xl leading-relaxed">{exam.tagline}</p>
          <div className="hero-rise-3 mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary-on-dark">
              {inDev ? 'Ask about availability' : 'Start preparing'}
            </Link>
            <Link to="/products/prep" className="btn-secondary-on-dark">
              How Prep works
            </Link>
          </div>
        </Container>
      </section>

      {/* Overview + registers */}
      <section className="py-16 md:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
            <div className="space-y-5">
              {exam.overview.map((p) => (
                <p key={p.slice(0, 40)} className="max-w-measure-wide leading-relaxed text-slate">
                  {p}
                </p>
              ))}
              <p className="text-sm text-faint">
                Content language{exam.languages.length > 1 ? 's' : ''}: {exam.languages.join(' and ')}.
              </p>
            </div>
            <div className="space-y-6 border-hairline lg:border-l lg:pl-10">
              <h2 className="text-sm font-semibold text-bright">In this exam’s voice</h2>
              {exam.registers.map((r) => (
                <div key={r.name}>
                  <h3 className="flex items-start gap-2 font-serif text-lg text-bright">
                    <span aria-hidden="true" className="mt-2.5 inline-block h-2 w-2 shrink-0 rounded-[1px] bg-seal" />
                    {r.name}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Blueprint, sourced to the regulator */}
      <section className="border-y border-hairline bg-ink-deep/40 py-16 md:py-24">
        <Container>
          <Mark>Weighted to the official specification</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            The blueprint is the curation instrument.
          </h2>
          <div className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
            <div>
              <p className="max-w-measure-wide leading-relaxed text-slate">{exam.blueprint.intro}</p>
              <ul className="mt-6 space-y-3">
                {exam.blueprint.points.map((pt) => (
                  <li key={pt} className="flex gap-2.5 leading-snug text-slate">
                    <span aria-hidden="true" className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-[1px] bg-seal" />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-media border border-hairline bg-surface p-6 self-start">
              <h3 className="text-sm font-semibold text-bright">The official source</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate">
                Prep’s scope for this exam follows the regulator’s own published specification. Read it at the
                source:
              </p>
              <ul className="mt-4 space-y-2">
                {exam.blueprint.sources.map((s) => (
                  <li key={s.url}>
                    <a href={s.url} target="_blank" rel="noreferrer" className="link-quiet text-sm font-semibold text-bright">
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 border-t border-hairline pt-3 text-xs leading-snug text-faint">
                {exam.regulator} defines what this exam requires. Prep operationalizes it.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* What is included */}
      <section className="py-16 md:py-24">
        <Container>
          <Mark>{inDev ? 'What it will include' : 'What a subscription includes'}</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">
            {inDev ? 'Everything the live exams get.' : 'Everything for this exam. Nothing generic.'}
          </h2>
          <ul className="mt-10 grid gap-x-12 gap-y-4 md:grid-cols-2">
            {included.map((item) => (
              <li key={item} className="flex gap-3 leading-relaxed text-slate">
                <span aria-hidden="true" className="mt-2 inline-block h-2 w-2 shrink-0 rounded-[1px] bg-seal" />
                {item}
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Exam FAQ */}
      {exam.faq && exam.faq.length > 0 && (
        <section className="border-t border-hairline py-16 md:py-24">
          <Container>
            <Mark>About this exam</Mark>
            <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
            <div className="mt-8 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
              {exam.faq.map((item, i) => (
                <div key={item.q}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                  >
                    <span className="font-semibold text-bright">{item.q}</span>
                    <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                  </button>
                  {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* CTA + other exams */}
      <section className="border-t border-hairline bg-ink-deep/40 py-16 md:py-20">
        <Container>
          <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="max-w-[24ch] font-serif text-3xl leading-tight">
                {inDev ? 'Want to know the moment it opens?' : 'Sitting this exam?'}
              </h2>
              <div className="mt-6">
                <Link to="/contact" className="btn-primary">
                  {inDev ? 'Ask about availability' : 'Start preparing'}
                </Link>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate">Other exams on Prep</p>
              <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {others.map((id) => (
                  <li key={id}>
                    <Link to={`/products/prep/${id}`} className="link-quiet text-sm font-semibold text-bright">
                      {examPages[id].name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
